/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
*
* View to swap the Live Widget Visualization through the ODT
* Options
* {
*    showTitles: boolean,  // Whether to show the titles for the available/recommended visualizations
*    visualizations: {
*      recommended: [],     // Array of visualizations to place in recommended section
*      other: []           // Array of visualizations to put in other section
*    },
*    currentVis: String,   // The id of the currently selected visualization
*    widget: Live Widget   // The widget that is creating the vischanger view
* }
*
**/

define([
	'../../../lib/@waca/core-client/js/core-client/ui/core/View',
	'../../../lib/@waca/core-client/js/core-client/utils/Utils',
	'../nls/StringResources',
	'jquery',
	'underscore',
	'react-dom',
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'dashboard-core/js/app/util/ErrorUtils',
	'@ba/ba-ui-carbon'
], function(BaseView, Utils, resources, $, _, ReactDOM, React, Toolkit, ErrorUtils, CARBON) {
	/* eslint react/prop-types: 0 */ // TODO: Remove once we have prop-types brought in from glass.
	const { Accordion } = Toolkit;
	const { AccordionItem } = Toolkit;
	const { SVGIcon } = Toolkit;
	const { Multilinetruncatedtext, TruncatedText } = Toolkit;
	const { ProgressIndicator } = Toolkit;
	const { ClickableTile } = CARBON;
	// Limit the number of recommended visualizations to show in the recommended section.
	const MaxRecommendedVisualizationsToShow = 6;
	const AUTO_ID = 'auto';
	const SECTION_ID = {
		RECOMMENDED: 'recommended',
		ALL: 'all'
	};

	/******************************************************
	 * Recommended Accordion Section
	 *	AccordionItem
	 *		[PropItems] - SVG (with possible deco) and Label
	 * All visualizations Accordion Section
	 * 	AccordionItem
	 *		[PropItems] - SVG (with possible deco) and Label
	********************************************************/
	/*
	* Class to render the react flyout components
	*/
	class ReactComponents extends React.Component {
		/*
		* The props hold a state that should be used as this classes state.
		* When the state changes the component gets re-rendered.
		*/
		constructor(props) {
			super(props);
			this.state = { ...props.state };
			this.settings = props.settings;
			this.onSelect = props.onSelect || function() {};
			this.state.checkMarkIcon = props.state.iconsFeature.getIcon('CheckmarkSVG');
		}

		/**
		* @param {object} item describing all the info needed to create a prop item
		* @param isRecommended true if the visualization is a recommended instance, otherwise false
		* @return {React Object}
		*/
		renderPropItem(item, isRecommended) {
			const selected = this.state.selectedId === item.id;
			const pressed = selected ? 'true':'false';
			let itemClassName = 'prop-item';
			if(selected) {
				itemClassName = `${itemClassName} selected`;
			}
			const SEPARATOR = ' ';
			const containSeparator = item.name && item.name.includes(SEPARATOR);
			const iconId = this.state.svgsMap[item.icon] ? this.state.svgsMap[item.icon].id : 'visualizations-changeVisualization';
			return (
				<div role={'group'} className={itemClassName}>
					<ClickableTile
						className='vis-type-tile'
						onSelect={() => this.onSelect(item, isRecommended)}
						handleKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === 'Space') {
								this.onSelect(item, isRecommended);
							}
						}}
					>
						<div tabIndex={0}
							className='prop-icon vis'
							role={'button'}
							data-id={item.id}
							walkme-data-id={item.id}
							aria-label={item.name}
							aria-pressed={pressed}>
							<SVGIcon
								iconId={iconId}
								height={24}
								width={24}>
							</SVGIcon>
							<SVGIcon
								className='checkMarkIcon'
								iconId={this.state.checkMarkIcon.id}
								height={14}
								width={14}>
							</SVGIcon>
							<div className='prop-icon-label-container'>
								{containSeparator ?
									(
										<Multilinetruncatedtext
											className='prop-icon-label'
											value={item.name}
											numLines={2}
										/>
									) : (
										<TruncatedText
											className='prop-icon-label'
											value={item.name}
										/>
									)
								}
							</div>
						</div>
					</ClickableTile>
				</div>
			);
		}

		/**
		* @param {Array} items all the visualizations to add to the row
		* @param {Boolean} isRecommended true iff the Vis Row is the recommended row.
		* @returns {Array} of React components for each Vis in the Vis Row.
		*/
		loadVisRowItems(items, isRecommended) {
			let svgs = [];
			items.forEach((item) => {
				svgs.push(this.renderPropItem(item, isRecommended));
			});

			return svgs;
		}

		/**
		* Render the progress indicator to show we are loading.
		*/
		_renderLoading() {
			return (
				<div className='vis-changer-progressIndicator'>
					<ProgressIndicator
						size={'normal'}
						variant={'circle'}
					/>
				</div>
			);
		}

		_getVisRowItems(options, maxHeight) {
			let { items, isRecommended } = options;
			const loadedSvgs = this.loadVisRowItems(items, isRecommended);
			const adjustedStyle = maxHeight ? { 'max-height': maxHeight } : {};
			return (
				<div className='visChangerRow' style={adjustedStyle}>
					{loadedSvgs}
				</div>
			);
		}

		_getRecommendedAccordionItem() {
			const recommendedOptions = {
				items: this.state.recommended.items,
				isRecommended: true
			};
			return this.state.recommended.isLoading ? this._renderLoading() : this._getVisRowItems(recommendedOptions, this._getMaxSectionHeight(SECTION_ID.RECOMMENDED));
		}

		_getAllVisAccordionItem() {
			const allVisOptions = {
				items: this.state.all.items,
				isRecommended: false
			};
			return this.state.all.isLoading ? this._renderLoading() : this._getVisRowItems(allVisOptions, this._getMaxSectionHeight(SECTION_ID.ALL));
		}

		_getSectionLabel(sectionId) {
			const sectionSettings = this.settings && this.settings[sectionId] || {};
			return sectionSettings.label;
		}

		_getSectionOpenState(sectionId) {
			/*
				Expand state is determined using a hierarchy. We first look to see if there
				is a user defined state (i.e. has the user explicitly expanded/collapsed a section).
				If there is a user defined state, use it. Otherwise, check the app defined
				settings.
				Basically, user settings override app settings.
			*/
			if (this.state.expandState.hasOwnProperty(sectionId)) {
				return this.state.expandState[sectionId];
			}
			const sectionSettings = this.settings && this.settings[sectionId] || {};
			return sectionSettings.expand !== false;
		}

		_getMaxSectionHeight(sectionId) {
			const sectionSettings = this.settings && this.settings[sectionId] || {};
			return sectionSettings.maxHeight;
		}

		/**
		* Render the collapsible sections (accordions)
		* @return {React Object} representing the Accordion and its sections.
		*/
		_renderAccordions() {
			let recommendedItem = this._getRecommendedAccordionItem();
			const isConsumer = this.state.isConsumer;
			const recommendedLabel = this._getSectionLabel(SECTION_ID.RECOMMENDED) || resources.get('recommended_visualizations');
			if (!isConsumer) {
				const allLabel = this._getSectionLabel(SECTION_ID.ALL) || resources.get('all_visualizations');
				return (
					<Accordion icon="right">
						<AccordionItem itemName={recommendedLabel} icon='left' open={this._getSectionOpenState(SECTION_ID.RECOMMENDED)} onChange={(isOpen) => this.state.onItemChange(isOpen, SECTION_ID.RECOMMENDED)}>
							{recommendedItem}
						</AccordionItem>
						<AccordionItem itemName={allLabel} icon='left' open={this._getSectionOpenState(SECTION_ID.ALL)} onChange={(isOpen) => this.state.onItemChange(isOpen, SECTION_ID.ALL)}>
							{this._getAllVisAccordionItem()}
						</AccordionItem>
					</Accordion>
				);
			} else {
				return (
					<Accordion>
						<AccordionItem itemName={recommendedLabel} icon='left' open={this._getSectionOpenState(SECTION_ID.RECOMMENDED)}>
							{recommendedItem}
						</AccordionItem>
					</Accordion>
				);
			}
		}

		/**
		* @returns {React Object} progress indicator if we are loading, accordions otherwise.
		*/
		render() {
			return this._renderAccordions();
		}
	}

	/**
	* The following view is the actual ODT Vis Changer view. It will handle
	* selection of the react components to change vis type, rendering the different
	* choices into two collapsible sections as well as showing a loading indicator
	* until all the SVGs are loaded.
	*/
	var View = BaseView.extend({

		/**
		* Load the SVG files representing all the visualizations
		*/
		_loadSVGFileMap() {
			// If we've already loaded the SVG files, return the map
			if (this.viewState.svgsMap && _.keys(this.viewState.svgsMap).length > 0) {
				return Promise.resolve(this.viewState.svgsMap);
			} else {
				this.viewState.svgsMap = {};
				this._getAllAvailableVisualizations();
				if(this.viewState.all && this.viewState.all.items){
					const iconsFeature = this.viewState.iconsFeature;
					this.viewState.all.items.forEach(item => {
						const visIcon = iconsFeature.getIcon(item.icon);
						this.viewState.svgsMap[item.icon] = visIcon;
					});
					if(!Object.keys(this.viewState.svgsMap).includes('visualizations-changeVisualization')){
						const autoIcon = iconsFeature.getIcon('visualizations-changeVisualization');
						this.viewState.svgsMap['visualizations-changeVisualization'] = autoIcon;
					}
					return Promise.resolve(this.viewState.svgsMap);
				}
			}
		},

		init: function(options) {
			// Provide the basic element.
			this.el = $('<div class=\'visChangerContainer\'/>');
			View.inherited('init', this, arguments);

			this.applyAction = options && options.actions && options.actions.apply;
			let finalOptions = options && options.state ? options.state : options;
			_.extend(this, finalOptions || {});
			this.iconsFeature = this.dashboard.getFeature('Icons');

			// Apply the initial flyout width from DashboardSettings
			// The 'visChanger' settings can be given in the following form:
			// {
			// 	width: '245px',				// width of the panel
			// 	recommended: {
			// 		label: 'We recommend',	// custom label for the recommended vis section
			// 		expand: true,			// expand state of the recommended vis section
			// 		maxHeight: '220px'		// maximum height of the recommended vis section
			// 	},
			// 	all: {
			// 		label: 'Other choices',	// custom label for the all vis section
			// 		expand: false,			// expand state of the all vis section
			// 		maxHeight: '420px'		// maximum height of the all vis section
			// 	}
			// }
			this.settings = this.dashboard.getFeature('DashboardSettings').get('visChanger') || {};
			this.el.style.width = this.settings.width || this.el.style.width;

			this._initializeViewState();
		},

		_elementExists: function() {
			let hasLength = false,
				hasKids = false,
				el = this.el;
			if (el) {
				if (el.length) {
					hasLength = el.length > 0;
				}
				if (el.children) {
					hasKids = el.children !== 0;
				}
			}
			return el && (hasKids || hasLength);
		},

		remove: function() {
			if (this._elementExists()) {
				ReactDOM.unmountComponentAtNode(this.el);
			}
			this.viewState = null;
			return View.inherited('remove', this, arguments);
		},

		setFocus: function(){
			this.$('.vis').first().focus();
		},

		_processVisualizations(visualizations) {
			let markupPayload = [];
			_.each(visualizations, vis => {
				this.viewState.visualizationMap[vis.id || vis.getId()] = vis;
				markupPayload.push({
					id: vis.id || vis.getId(),
					type: vis.type || (vis.getType && vis.getType()),
					name: vis.caption || vis.getLabel(),
					visId: vis.id || vis.getId(),
					caption: vis.caption || vis.getLabel(),
					icon: vis.icon || vis.getIcon(),
					disabled: vis.disabled || false, // default: NOT disabled,
					properties: vis.properties || []
				});
			});
			return markupPayload;
		},

		/**
		* We want to dynamically load all the SVGs (why load them if we never need them)
		* This method loads the SVG files into a map, asks the widget for a list
		* of recommended and non-recommended visualizations, and adds the 'Auto'
		* choice as well.
		*/
		_preload: function() {
			// Load the file map
			this.viewState.visualizationMap = this.viewState.visualizationMap || {};
			return this._loadSVGFileMap();
		},

		_getAllAvailableVisualizations() {
			let allVisualizations = this.dashboard.getFeature('VisDefinitions').getList().filter(definition => {
				if (definition.getProperty) {
					return definition.getProperty('recommendOnly') !== true &&
						definition.getProperty('isCustomVis') !== true &&
						definition.getProperty('isSchematic') !== true;
				}
				return true;
			});
			let processedItems = this._processVisualizations(allVisualizations);
			this.viewState.all.items = _.sortBy(processedItems, vis => {return vis.name;});
			this.viewState.all.isLoading = false;
		},

		_getRecommendedVisualizations() {
			return this.content.getFeature('Visualization.SmartsRecommender').getRecommendedVisualizations()
				.then(recommended => {
					if(this.viewState === null) {
						return; //Remove has already been called, stop processing
					}
					let processedVisualizations = this._processVisualizations(recommended);
					let maxVisualizationsToShow = MaxRecommendedVisualizationsToShow;
					if (this.viewState.selectedId) {
					// Don't include Auto in the limit
						maxVisualizationsToShow++;
					}
					this.viewState.recommended.items = processedVisualizations.slice(0, maxVisualizationsToShow);
					this.viewState.recommended.isLoading = false;

					// cache recommended vis icons that may not already be cached from "All visualization list"
					this.viewState.recommended.items.forEach(item => {
						if (!this.viewState.svgsMap[item.icon]) {
							this.viewState.svgsMap[item.icon] = this.viewState.iconsFeature.getIcon(item.icon);
						}
					});
				});
		},

		_initializeViewState() {
			this.viewState = {
				recommended: {
					items: [],
					isLoading: true
				},
				all: {
					items: [],
					isLoading: true
				},
				svgsMap: null,
				selectedId: this._getSelectedVizId(),
				visualizationMap: null,
				iconsFeature: this.iconsFeature,
				expandState: this.expandState,
				onItemChange: this._onItemChange.bind(this)
			};
		},

		/*
			Update the user defined expand state with as specified.
		*/
		_setSectionOpenState(isOpen, sectionId) {
			this.expandState[sectionId] = isOpen;
		},

		//called when an item in the accordian is changed
		_onItemChange(isOpen, sectionId) {
			this._setSectionOpenState(isOpen, sectionId);
			//ensure this flyout view is not off the screen
			const diff = this._getOffScreenHeight();
			if (diff > 0) {
				//TODO: This is not a great solution, especially if we change the way popovers are handled.
				// in the future, any resizing should be handled by the popover class.
				let popover = $(this.el).parents('.popover-content');
				let top = Math.max(0, popover.position().top - diff);
				popover && popover.css( { top: top } );
			}
		},

		/**
		 * Checks to see if this View is off the visible screen.
		 * @returns the number of pixels this view is off the screen, or -1 if it is not off the screen
		 */
		_getOffScreenHeight() {
			let htmlScrollHeight = this._getDocumentElement().scrollHeight;
			let innerHeight = Utils.getCurrentWindow().innerHeight;
			if (htmlScrollHeight > innerHeight) {
				return htmlScrollHeight - innerHeight;
			}
			return -1;
		},

		/**
		 * @returns The document.documentElement.
		 *  This is here to more easily mock for tests.
		 */
		_getDocumentElement() {
			return document.documentElement;
		},

		_getSelectedVizId() {
			// A unique visId.alias can be used to dynamically list multiple visualizations
			// based on the same visDefinition. VisualizationSmartsRecommenderAPI implementation
			// has the responsibility to set the 'visId.alias' when
			// multiple instance of the recommended vis is given based on the same visDefinition.
			// Here the visSwitcher needs to ensure the correct visualization is marked as selected
			// by referring to the 'visId.alias' if such property exists.
			// Otherwise continue with the visId provided by the 'VisDefinitionAPI.getId'
			let selectedVisId = this.content.getPropertyValue('visId.alias');
			if (!selectedVisId) {
				let visualization = this.content.getFeature('Visualization');
				if ((this.currentVis && this.currentVis === AUTO_ID) || (visualization && !visualization.isTypeLocked())) {
					selectedVisId = AUTO_ID;
				} else {
					selectedVisId = visualization.getDefinition().getId();
				}
			}
			return selectedVisId;
		},

		_resetRecommendedViewState() {
			this.viewState.recommended = {
				items:[],
				isLoading: true
			};
			if (this._elementExists() && this.reactComponents) {
				ReactDOM.unmountComponentAtNode(this.el);
			}
		},

		/**
		* Create the react components with an initial loading state.
		*/
		_showLoading() {
			this._resetRecommendedViewState();
			// Render the react components
			this.reactComponents = ReactDOM.render(
				<ReactComponents state={this.viewState} settings={this.settings} onSelect={this._selectVis.bind(this)}/>,
				this.el
			);

		},

		/**
		* Render the react components with a state to show the accordions.
		*/
		_showReactSections() {
			this.reactComponents.setState(JSON.parse(JSON.stringify(this.viewState)));
		},

		_render(options) {
			_.extend(this, options || {});
			this._isConsumer();
			this._showLoading();
			return this._preload()
				.then(() => {
					this._showReactSections();
				})
				.then(this._getRecommendedVisualizations.bind(this))
				.then(() => {
					this._showReactSections();
				});
		},

		_isConsumer() {
			const currentContentView = this.dashboard && this.dashboard.getCurrentContentView();
			const glassContext = currentContentView && currentContentView.glassContext;
			if (this.viewState && glassContext) {
				this.viewState.isConsumer = !ErrorUtils.hasCapability(glassContext, 'canAuthorDashboard');
			}
		},

		/**
		* Method to render the view. This includes creating the react components to
		* initially show a progress indicator, preload all the svg icons, divide the
		* choices into sections, then update the react components to show the accordions.
		*/
		render: function (options) {
			this._render(options);
			return Promise.resolve();
		},

		// when id auto we highlight auto too
		/**
		 * Event handler for a .vis button is clicked.
		 * Send event to be handled by Live Widget and updates its view with current selection
		 */
		_selectVis: function(item, isRecommended) {
			// Only act when the newly selected vis type is different
			if (this.viewState.selectedId !== item.id) {
				const type = item.type || item.id;
				const icon = type !== AUTO_ID ? this.viewState.svgsMap[item.icon] : undefined;
				this.applyAction(type, {
					recommendedCategory: isRecommended ? 'Recommended' : 'Other',
					properties: item.properties,
					expandState: this.expandState, // Update action with the current expand state
					icon: icon
				});
				if (this._toolbar) {
					// Remove the view and hide the associated toolbar that is added with every nextView.
					this.remove();
					this._toolbar.hide();
				}
			}
		},

		preload() {
			return Promise.resolve();
		},
		renderCallBack: function(toolbar) {
			/*
				We want to remove the view when a new vis is selected. There is currently
				an issue with next views and the toolbar where it will not automatically
				close on applyAction. To get around this in the short term (Ken is creating
				a tech debt item for Core) we will store the toolbar here and hide it
				when a selection occurs.
			*/
			this._toolbar = toolbar;
		}

	});

	return View;

});
